import { Box, Button, Toolbar as MUIToolbar, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material/styles';
import { Modal } from 'components';
import moment from 'moment';
import React, { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.module.css';
import { CalendarProvider } from './CalendarContext ';

const localizer = momentLocalizer(moment);

type Props = {
    events: any;
    CustomEvent: any;
};

const SGSCalendar: React.FC<Props> = ({ events, CustomEvent }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [eventsList, setEventsList] = useState([]);
    // get the last event
    const maxDateVirement = events ? events.reduce((maxDate, currentDate) => (currentDate > maxDate ? currentDate : maxDate), events[0]) : null;
    // Determine the default date
    const defaultDate = new Date(maxDateVirement?.end) ?? new Date();

    const CustomEventWrapper = ({ continuesPrior, continuesAfter, children }) => {
        // Customize the event wrapper here
        const paddingLeft = continuesPrior ? '0' : '10px';
        const paddingRight = continuesAfter ? '0' : '10px';

        const wrapperStyle = {
            paddingLeft,
            paddingRight,
        };

        return <Box style={wrapperStyle}>{children}</Box>;
    };

    const messages = {
        showMore: (total) => `+ ${total} en plus`,
    };

    const handleShowMoreClick = (events) => {
        setEventsList(events);
        setIsModalOpen(true);
    };

    const IndeminitesTable = () => {
        const StyleTable = styled(Table)(() => ({
            border: '1px solid #cdcdcd',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
        }));
        const StyledTableRow = styled(TableRow)(() => ({
            backgroundColor: '#E1F1FC',
            borderBottom: 'none',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
        }));

        const StyledTableCell1 = styled(TableCell)(() => ({
            color: '#2574A9',
            background: '#F5F5F5',
            fontSize: '0.83rem ',
            fontFamily: 'Open Sans ',
            fontStyle: 'normal ',
            fontWeight: 700,
            textAlign: 'center',
            borderRight: '1px solid #cdcdcd',
            borderBottom: '1px solid #cdcdcd',
            borderTop: 'none',
            '&:last-child': {
                borderRight: 'none', // Remove the right border for the last cell
            },
        }));

        const StyledTableCell = styled(TableCell)(() => ({
            color: '#4F7793',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
            fontSize: '0.85rem',
            textAlign: 'center',
            verticalAlign: 'top',
            paddingTop: '5px',
            height: '54px',
            paddingBottom: '3px',
            borderBottom: 'none',
            borderRight: '1px solid #cdcdcd',
            borderTop: '1px solid #cdcdcd',
            '&:last-child': {
                background: '#3399DB',
                borderRight: 'none', // Remove the right border for the last cell
                color: '#FFF',
                fontWeight: 700,
            },
        }));

        return (
            <StyleTable aria-label="simple table">
                <TableHead>
                    <StyledTableRow>
                        <StyledTableCell1>Période du sinistre</StyledTableCell1>
                        <StyledTableCell1 align="right">Nature</StyledTableCell1>
                        <StyledTableCell1 align="right">Payé le</StyledTableCell1>
                        <StyledTableCell1 align="right">Nombre de jours de prestations</StyledTableCell1>
                        <StyledTableCell1 align="right">Montant net</StyledTableCell1>
                    </StyledTableRow>
                </TableHead>
                <TableBody>
                    {eventsList.map((row) => (
                        <TableRow key={row.id}>
                            <StyledTableCell component="th" scope="row">
                                {moment(row.start).format('MM/DD/YYYY')} - {moment(row.end).format('MM/DD/YYYY')}
                            </StyledTableCell>
                            <StyledTableCell align="right">{row.title}</StyledTableCell>
                            <StyledTableCell align="right">{moment(row.dateVirement).format('MM/DD/YYYY')}</StyledTableCell>
                            <StyledTableCell align="right">{row.nbreJourArret}</StyledTableCell>
                            <StyledTableCell align="right">{formattedNumber(row.montantNet)}</StyledTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </StyleTable>
        );
    };

    return (
        <>
            <CalendarProvider>
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    defaultDate={defaultDate}
                    showAllEvents={false}
                    views={['month']}
                    onShowMore={handleShowMoreClick}
                    components={{
                        event: CustomEvent,
                        eventWrapper: CustomEventWrapper,
                        toolbar: CustomToolbar,
                        dateCellWrapper: ColoredDateCellWrapper,
                        month: {
                            header: CustomDayHeader,
                        },
                    }}
                    eventPropGetter={eventStyleGetter}
                    style={{ fontSize: '.8rem' }}
                    messages={messages}
                />
            </CalendarProvider>
            <Modal setIsOpenModal={() => setIsModalOpen(false)} title={'Indemnisations par période'} isOpenModal={isModalOpen}>
                <IndeminitesTable />
            </Modal>
        </>
    );
};

export default SGSCalendar;

const CustomDayHeader = ({ date, localizer }) => {
    return <Box style={{ color: '#337ab7', fontFamily: 'Open sans', fontWeight: 'bold', padding: '2px' }}>{localizer.format(date, 'ddd')}</Box>;
};

const eventStyleGetter = (event, start, end, isSelected) => {
    const style = {
        marginTop: '20px',
        height: isSelected ? '21px' : '19px',
        fontSize: '.85em',
        padding: '0',
        borderRadius: '2px',
        paddingLeft: '0px',
        paddingRight: '0px',
        backgroundColor: '#2574a9',
        border: isSelected ? '2px solid #13496d' : '1px solid #3788d8',
        outline: 'none',
        color: 'white',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    return {
        style,
        event,
        start,
        end,
    };
};

const ColoredDateCellWrapper = ({ children }) => {
    return React.cloneElement(React.Children.only(children), {
        style: {
            ...children.style,
            backgroundColor: '#fff',
        },
    });
};

const CustomToolbar = (toolbar) => {
    const goToNext = () => {
        toolbar.onNavigate('NEXT');
    };

    const goToPrev = () => {
        toolbar.onNavigate('PREV');
    };

    return (
        <MUIToolbar>
            <Typography variant="h6" style={{ flex: 1, textAlign: 'left', textTransform: 'capitalize', fontFamily: 'Open sans', fontWeight: 'bold' }}>
                {toolbar.label}
            </Typography>
            <Button onClick={goToPrev} style={{ minWidth: '20px', background: '#2C3E50', borderRadius: '5px 0 0 5px' }}>
                <ChevronLeft style={{ color: '#fff' }} />
            </Button>
            <Button onClick={goToNext} style={{ minWidth: '20px', background: '#2C3E50', borderRadius: '0 5px 5px 0 ' }}>
                <ChevronRight style={{ color: '#fff' }} />
            </Button>
        </MUIToolbar>
    );
};

const formattedNumber = (number) => {
    const numberAsFloat = parseFloat(number);
    if (!isNaN(numberAsFloat)) {
        return numberAsFloat.toLocaleString('fr-FR', {
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } else {
        return 'Invalid input';
    }
};
